import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Loader2, Check, ImagePlus } from "lucide-react";
import api from "@/lib/api";
import { fileUrl, uploadFileChunked } from "@/lib/uploads";
import { compressImage } from "@/lib/imageCompress";
import { bgByKey } from "@/lib/storyBg";
import { useI18n } from "@/context/I18nContext";
import { toast } from "sonner";

function StoryThumb({ s, selected, onClick }) {
  const bg = s.type === "text" ? bgByKey(s.bg) : null;
  return (
    <button onClick={onClick} data-testid={`hl-pick-${s.id}`}
      className={`relative rounded-xl overflow-hidden aspect-[9/16] border-2 ${selected ? "border-[var(--rc-gold)]" : "border-transparent"}`}>
      {s.type === "text"
        ? <div className="absolute inset-0 grid place-items-center px-1 text-center text-[10px] font-bold" style={{ background: bg.css, color: bg.fg }}>{(s.text || "").slice(0, 40)}</div>
        : <img src={fileUrl(s.poster || s.media)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
      {s.expired && <span className="absolute top-1 left-1 text-[8px] bg-black/60 text-white/80 rounded px-1">expired</span>}
      {selected && <span className="absolute top-1 right-1 h-5 w-5 grid place-items-center rounded-full bg-[var(--rc-gold)] text-black"><Check size={13} strokeWidth={3} /></span>}
    </button>
  );
}

export default function HighlightEditor({ onClose, onCreated }) {
  const { t } = useI18n();
  const [stories, setStories] = useState(null);
  const [sel, setSel] = useState([]);
  const [title, setTitle] = useState("");
  const [cover, setCover] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const coverRef = useRef(null);

  useEffect(() => { api.get("/social/stories/mine/all").then(({ data }) => setStories(data)).catch(() => setStories([])); }, []);

  const toggle = (id) => setSel((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);

  const pickCover = async (e) => {
    let f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try { f = await compressImage(f); const { url } = await uploadFileChunked(f, null, "social"); setCover(url); }
    catch { toast.error(t("st_cover_failed")); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const create = async () => {
    if (!sel.length) { toast.error(t("st_pick_story")); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/social/highlights", { title: title.trim() || "Highlight", story_ids: sel, cover: cover || undefined });
      toast.success(t("st_hl_saved")); onCreated && onCreated(data); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || t("st_failed")); setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[120] bg-black/70 flex items-end sm:items-center justify-center" onClick={onClose} data-testid="highlight-editor">
      <div onClick={(e) => e.stopPropagation()} className="w-full sm:max-w-md bg-[var(--rc-surface)] rounded-t-2xl sm:rounded-2xl p-4 max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between mb-3">
          <span className="font-heading text-xl text-[var(--rc-gold)]">{t("st_new_highlight")}</span>
          <button onClick={onClose} data-testid="highlight-editor-close" className="text-[var(--rc-muted)]"><X size={20} /></button>
        </div>

        <div className="flex items-center gap-3 mb-3">
          <input ref={coverRef} type="file" accept="image/*" hidden onChange={pickCover} data-testid="highlight-cover-input" />
          <button onClick={() => coverRef.current?.click()} data-testid="highlight-cover-btn"
            className="relative h-16 w-16 shrink-0 rounded-full overflow-hidden border-2 border-dashed border-[var(--rc-gold)]/50 grid place-items-center text-[var(--rc-gold)]">
            {uploading ? <Loader2 size={18} className="animate-spin" /> : cover ? <img src={fileUrl(cover)} alt="" className="h-full w-full object-cover" /> : <ImagePlus size={20} />}
          </button>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("st_hl_name_ph")} maxLength={30} data-testid="highlight-title-input"
            className="flex-1 rounded-full bg-[var(--rc-bg)] border border-[var(--rc-border)] px-4 py-2.5 outline-none focus:border-[var(--rc-gold)]/50" />
        </div>
        <p className="text-[11px] text-[var(--rc-muted)] -mt-1 mb-2">{t("st_hl_cover_hint")}</p>

        <p className="text-xs text-[var(--rc-muted)] mb-2">{t("st_hl_choose")} ({sel.length} {t("st_hl_selected")})</p>
        <div className="flex-1 overflow-y-auto no-scrollbar">
          {stories === null ? <div className="py-10 text-center"><Loader2 className="animate-spin inline text-[var(--rc-gold)]" /></div>
            : stories.length === 0 ? <div className="py-10 text-center text-[var(--rc-muted)] text-sm">{t("st_hl_none")}</div>
              : <div className="grid grid-cols-3 gap-2">{stories.map((s) => <StoryThumb key={s.id} s={s} selected={sel.includes(s.id)} onClick={() => toggle(s.id)} />)}</div>}
        </div>
        <button onClick={create} disabled={busy || !sel.length} data-testid="highlight-create-btn"
          className="mt-3 w-full flex items-center justify-center gap-2 rounded-full bg-[var(--rc-gold)] text-black font-semibold py-3 disabled:opacity-50">
          {busy && <Loader2 size={16} className="animate-spin" />} {t("st_save_highlight")}
        </button>
      </div>
    </div>,
    document.body
  );
}
